import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Card, Divider, Button, Tooltip, Col, Row, InputNumber, Form, App, Flex, Typography, Space, } from 'antd';
import { IdcardOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { txt } from 'libs/i18n';
import clsx from 'clsx';
import { abandonProjectsIn, changeParallelTranscriptionLimit, fetchParallelTranscriptionLimit, fetchIsUserDeletable, fetchStorageStatistics, changeDirectTranscriptionLimit, fetchDirectTranscriptionLimit, deleteUser, } from 'api/admin/users-api';
import { Alerts } from 'components/Alerts';
import DeleteModal from 'components/DeleteModal';
import StorageStatisticsInfo from 'components/StorageStatisticsInfo';
import { useSession } from 'components/Authenticated';
import AbandonProjects from 'screens/DashboardScreen/components/AbandonProjects';
import BasicUserInformation from './BasicUserInformation';
import UserClaims from './UserClaims';
import styles from '../style.module.less';
const { Text } = Typography;
const UserProfile = ({ onRoleChange, user, teamOwner, alert, claimSet, onRefetchProfile, onRefetchClaims, }) => {
    const { session } = useSession();
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [storageStatistics, setStorageStatistics] = useState('noshow');
    const [transcriptionLimits, setTranscriptionLimits] = useState({
        parallel: 1,
        direct: 1,
    });
    const [denyDelete, setDenyDelete] = useState({ reason: 'None' });
    const [abandonProjects, setAbandonProjects] = useState({
        enable: user.abandonProjectsIn !== null,
        inDays: user.abandonProjectsIn !== null ? Number(user.abandonProjectsIn.split('.')[0]) : null,
    });
    const [userDataOnly, setUserDataOnly] = useState(true);
    const { message } = App.useApp();
    const loadTranscriptionLimits = async () => {
        setTranscriptionLimits({
            parallel: await fetchParallelTranscriptionLimit(session.connection, user.id),
            direct: await fetchDirectTranscriptionLimit(session.connection, user.id),
        });
    };
    const fetchUserDeleteBlock = async () => {
        setDenyDelete(await fetchIsUserDeletable(session.connection, user.id));
    };
    useEffect(() => {
        void loadTranscriptionLimits();
        void fetchUserDeleteBlock();
    }, []);
    const handleLimitChange = (value, limitType) => {
        if (value !== null) {
            setTranscriptionLimits(Object.assign(Object.assign({}, transcriptionLimits), { [limitType]: value }));
        }
    };
    const handleResetDeletion = async (resetValue) => {
        const result = await abandonProjectsIn(session.connection, user.id, resetValue);
        if (result.isSuccess()) {
            void message.success(txt('cancelAutomaticalDeletion'));
        }
        else {
            void message.error(txt('resetAutomaticalDeletionFail'));
        }
    };
    const handleAbandonProjectsChange = (changedValues, isReset) => {
        setAbandonProjects(changedValues);
        if (isReset === true) {
            void handleResetDeletion(changedValues);
        }
    };
    const handleAbandonProjects = async () => {
        if (abandonProjects.inDays === null) {
            void message.error(txt('missingDays'));
            return;
        }
        const result = await abandonProjectsIn(session.connection, user.id, abandonProjects);
        if (result.isSuccess()) {
            void message.success(txt('successAutomaticalDeletion'));
        }
        else {
            void message.error(txt('settingAutomaticalDeletionFail'));
        }
    };
    const updateTranscriptionLimit = async (limitType) => {
        if (limitType === 'parallel') {
            await changeParallelTranscriptionLimit(session.connection, user.id, transcriptionLimits.parallel);
            void message.success(`${txt('succesfullyChangedTranscriptionLimit')} ${transcriptionLimits.parallel}`);
        }
        else {
            await changeDirectTranscriptionLimit(session.connection, user.id, transcriptionLimits.direct);
            void message.success(`${txt('succesfullyChangedTranscriptionLimit')} ${transcriptionLimits.direct}`);
        }
    };
    const fetchStorageStatisticsInfo = async (onlyUserData) => {
        setStorageStatistics('loading');
        const data = await fetchStorageStatistics(session.connection, user.id, onlyUserData);
        setStorageStatistics({ storagePerCount: data.bytesPerShareCount, onlyUserData });
    };
    const handleStorageInfoChange = async (value) => {
        setUserDataOnly(value);
        await fetchStorageStatisticsInfo(value);
    };
    const getTooltipTitle = () => {
        if (denyDelete.reason === 'Owner') {
            return txt('cannotDeleteOwner');
        }
        if (denyDelete.reason === 'Self') {
            return txt('cannotDeleteSelf');
        }
        return '';
    };
    const handleDeleteUser = async (userId, email) => {
        const result = await deleteUser(session.connection, userId);
        if (result.isSuccess()) {
            setDeleteModalVisible(false);
            void message.success(`${txt('user')}${email}${txt('succesfullyDeleted')}`, 3);
        }
        else {
            void message.error(txt('failedToDelete'), 2);
        }
    };
    return (_jsxs(Card, { className: styles.card, bordered: false, title: (_jsxs(Flex, { justify: "space-between", align: "center", children: [_jsxs(Space, { children: [_jsx(IdcardOutlined, {}), _jsx("h2", { children: txt('userProfile') })] }), _jsx(Text, { children: `User id: ${user.id}` })] })), children: [alert.isVisible && Alerts.crash(alert.text === null ? '' : txt(alert.text)), _jsx("div", { className: styles.sectionName, children: txt('basicInfo') }), _jsx(Divider, { className: styles.divider }), _jsx(BasicUserInformation, { user: user, onRoleChange: onRoleChange, teamOwnerEmail: teamOwner, onRefetchProfile: onRefetchProfile }), _jsx("div", { className: styles.sectionName, children: txt('transcriptionLimit') }), _jsx(Divider, { className: styles.divider }), _jsxs(Form, { wrapperCol: { sm: { offset: 3 } }, labelCol: { sm: { span: 9 } }, labelAlign: "left", labelWrap: true, children: [_jsxs(Form.Item, { label: txt('currentTranscriptionLimit'), children: [_jsx(InputNumber, { value: transcriptionLimits.parallel, onChange: (value) => handleLimitChange(value, 'parallel'), min: 1, max: 100, precision: 0 }), _jsx(Button, { className: styles.saveButton, type: "primary", htmlType: "submit", onClick: () => updateTranscriptionLimit('parallel'), children: txt('save') })] }), _jsxs(Form.Item, { label: txt('directTranscriptionLimit'), children: [_jsx(InputNumber, { value: transcriptionLimits.direct, onChange: (value) => handleLimitChange(value, 'direct'), min: 1, max: 100, precision: 0 }), _jsx(Button, { className: styles.saveButton, type: "primary", htmlType: "submit", onClick: () => updateTranscriptionLimit('direct'), children: txt('save') })] })] }), _jsx("div", { className: styles.sectionName, children: txt('claims') }), _jsx(Divider, { className: styles.divider }), _jsx(UserClaims, { userId: user.id, claimSet: claimSet, onRefetchClaims: onRefetchClaims }), _jsx("div", { className: styles.sectionName, children: txt('projectDeletion') }), _jsx(Divider, { className: styles.divider }), _jsx(AbandonProjects, { value: abandonProjects, onChange: handleAbandonProjectsChange, hasInfoCircle: true, isTeamOwner: session.login.hasClaim('team:owner'), children: _jsx(Tooltip, { title: abandonProjects.enable === null || !abandonProjects.enable ? txt('enableDeletion') : null, children: _jsx(Button, { className: styles.saveButton, type: "primary", onClick: handleAbandonProjects, disabled: abandonProjects.enable === null || !abandonProjects.enable, children: txt('save') }) }) }), _jsxs(Row, { children: [_jsx(Col, { xs: 10, sm: 12 }), _jsx(Col, { children: _jsx("div", { className: styles.abandonProjectsDetail, children: _jsx("div", { children: `(${txt('userDeletionDeadline')}: ${user.abandonProjectsIn === null ? txt('none') : user.abandonProjectsIn.split('.')[0]})` }) }) })] }), _jsx("div", { className: styles.sectionName, children: txt('storage') }), _jsx(Divider, { className: styles.divider }), _jsx(Row, { children: _jsx(Button, { type: "primary", onClick: () => fetchStorageStatisticsInfo(userDataOnly), children: txt('displayStorageStatistics') }) }), _jsx("div", { className: styles.storageInfo, children: _jsx(StorageStatisticsInfo, { storageStatistics: storageStatistics, onStorageStatisticsChange: handleStorageInfoChange, userDataOnly: userDataOnly }) }), _jsx("div", { className: clsx(styles.sectionName, styles.sectionNameDanger), children: txt('deleteUser') }), _jsx(Divider, { className: styles.divider }), _jsxs(Row, { children: [_jsx(Col, { span: 12, children: _jsx(Tooltip, { title: getTooltipTitle(), overlayStyle: denyDelete.reason !== 'None' ? {} : { display: 'none' }, children: _jsxs(Button, { className: denyDelete.reason !== 'None' ? '' : 'ant-btn-danger', onClick: () => setDeleteModalVisible(true), disabled: denyDelete.reason !== 'None', children: [_jsx(UserDeleteOutlined, {}), txt('deleteUserPermanently')] }) }) }), _jsx(DeleteModal, { visible: deleteModalVisible, onCancel: () => setDeleteModalVisible(false), email: user.email, isTeamOwner: false, onDelete: async () => handleDeleteUser(user.id, user.email), redirectTo: "/admin/users" })] })] }));
};
export default UserProfile;
